/**
 * Asset Library 域型別 — 素材庫(`asset_libraries/{lib}/items/{item}` = LibraryItem)。
 * -----------------------------------------------------------------------------
 * 對齊後端 `app/asset_libraries` router 的 FE-facing response(`AssetLibraryResponse` /
 * `LibraryItemResponse`;2026-05-26 G3 audit 後已去 internal 欄位:owner_uid /
 * group_owner_uid / version / schema_version / created_by_cuid)。group-scoped
 * (`/api/asset_libraries` 非 GROUP_HEADER_EXEMPT → `httpRequestWithRefresh` 自動
 * 帶 `X-Current-Group`)。
 *
 * ⚠️ 與 canvas 生成的 **ProjectAsset**(`AssetWithJobContext`,`assets.types.ts`)是
 * 不同 domain:
 *   - **LibraryItem** — raw 上傳素材,可重用 import 進多 project;T7 推上 GA 素材庫的來源
 *   - **ProjectAsset** — DAG canvas 生成,project-scoped
 *
 * 對應 service:`assetLibrariesService`(list libraries / list items)。
 */

import type { CheckBadge } from './check';
import { mapCheckBadgeDto, type CheckBadgeDto } from './check.mappers';
import type { CopyAssets } from './canvasAi.types';
import type { CopyAssetsDto } from './canvasAi.dtos';
import { mapCopyAssetsDto } from './canvasAi.mappers';

export type LibraryItemKind = 'TEXT' | 'IMAGE' | 'VIDEO' | 'COMPOSE';

/**
 * 素材角色(typed 欄;值域 = BE `app/workflow/slot_roles.SlotRole`)。
 * `reference` 是 ADR-035 舊泛用圖片格的相容值,新寫入不用。
 */
export type LibraryItemRole = 'product' | 'character' | 'scene' | 'extra' | 'reference';

const LIBRARY_ITEM_ROLES: readonly string[] = [
  'product',
  'character',
  'scene',
  'extra',
  'reference',
];

/** 未知角色(BE 加值域而 FE 沒跟上)→ 當作沒有角色,不猜。 */
export function isLibraryItemRole(v: unknown): v is LibraryItemRole {
  return typeof v === 'string' && LIBRARY_ITEM_ROLES.includes(v);
}
/**
 * 對齊 BE `LibraryItemResponse.source`(`asset_libraries/models.py:504`)。
 *
 * ⚠️ **值域漏一個 = 靜默壞版面**:六個 render 點都是 `t(\`libraryView.source.${source}\`)`,
 * i18n 缺鍵時 fallbackLng 也是 zh-TW 自己 → 畫面直接印出 key 字串;而 `SOURCE_OPTIONS`
 * 缺該值會讓那批 item 一按來源 chip 就被濾光且篩不回來。`template_demo`(docs/86 /
 * ADR-042 平台範本示範素材)就是這樣落後 BE 一輪的 —— BE 那邊有回歸鎖
 * `test_response_source_literal_covers_entity`,FE 這邊靠這行註解。
 */
export type LibraryItemSource =
  | 'upload'
  | 'ad_account_import'
  | 'project_asset'
  | 'template_demo'
  /**
   * R270 / ADR-044(BE docs/95 §2 ③)— 畫布**文案藍卡**經
   * `POST /asset_libraries/{lib_id}/items/from_node` 入庫。與 `project_asset` 的差別是
   * 來源指標:那個指 `ProjectAsset`,這個指 node(文案卡沒有 asset —— `/text-gen` 是同步
   * 端點,不建 asset、不寫 GCS)。
   */
  | 'canvas_node';

// ─── DTO(snake_case wire)──────────────────────────────────────────────────

export interface AssetLibrarySummaryDto {
  lib_id: string;
  name: string;
  visibility: string;
  group_id: string;
  is_owner: boolean;
  deleted_datetime: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssetLibrariesListResponseDto {
  items: AssetLibrarySummaryDto[];
}

export interface LibraryItemDto {
  item_id: string;
  asset_kind: LibraryItemKind;
  source: LibraryItemSource;
  source_ad_account_cid: string | null;
  /** `source="project_asset"`(canvas 成品反向存入,MR !41 S-c)時填反查欄。 */
  source_project_asset_id?: string | null;
  source_project_id?: string | null;
  /** `source="canvas_node"`(R270 / ADR-044)時填產出它的文案藍卡 nid。 */
  source_project_node_id?: string | null;
  /** raw GCS URI;FE 不直連(signed URL 由 backend 簽),僅供 metadata 顯示。 */
  gcs_uri: string;
  metadata: Record<string, unknown>;
  hash_method: Record<string, string>;
  is_creator: boolean;
  created_at: string;
  // ── 檢查域補欄(docs/88 §9 / docs/87 §4;`GET /asset_libraries/items` 起)──
  // 一律 optional + nullable:改版前的 build 不回這幾欄,讀不到就當「未檢查 / 未上傳 /
  // 無專案名」,不能炸(既有 `source_project_asset_id?` 同慣例)。
  /** 「檢查結果」欄(免逐筆拉報告);**欄位缺席 = 未檢查**,不是某個 `s` 值。 */
  check_badge?: CheckBadgeDto | null;
  /** 「檢查日期」欄(ISO)。badge 的 `d` 是已格式化字串,這欄才是可排序的時間。 */
  checked_at?: string | null;
  /** 「上傳廣告」欄(ISO;null = 尚未上傳 — T7 上傳成功時由 BE denormalize)。 */
  ga_uploaded_at?: string | null;
  /**
   * 「上傳廣告」欄的 **Meta** 對應(ISO;null = 尚未上傳到 Meta)。
   * BE `asset_libraries/models.py` + `ads/meta/upload_repository.py` B16 denormalize。
   *
   * ⚠️ **與 `ga_uploaded_at` 是兩個獨立事實**,不是二選一:同一顆素材可以只上 Google、
   * 只上 Meta、兩個都上、或都沒上。合成一個布林(「有沒有上傳過」)會讓使用者分不出
   * 「還沒上 Meta」與「兩邊都上了」。
   * ⚠️ **上線前既有的舊上傳紀錄不回填**(BE docs/122 §2 ③)—— 舊素材顯示 null 是資料
   * 事實,不是 bug。
   */
  meta_uploaded_at?: string | null;
  /** 「來源名稱」欄:僅 `source="project_asset"` 有值(專案已刪 → null 顯「—」)。 */
  source_project_name?: string | null;
  /**
   * latest 報告 id(BE `models.py:520`)。**報告頁的首選路徑** —— 沒有它就得靠
   * `checkBatchStore` 裡那份 30 分 TTL 的 batch,重載/隔天/換裝置/跑過第二批之後
   * 幾乎必然拿不到,只能讓 BE 走 composite index 查最新(index 未部署 = error 態)。
   */
  check_report_id?: string | null;
  /**
   * 檢查報告「套用修改建議」的套用版(BE `asset_libraries/models.py` `applied_texts`;
   * copy-apply 成功時由報告 doc 同批抄過來)。key = `${CopyCategory}:${rowIdx}`。
   * R419.1(BE v22-183)起套用**直接寫回素材檔**,這欄只標「哪幾則已套用」;改版前的舊資料
   * (只存報告、沒寫進檔)仍要疊,判準見 [appliedTextOverlay](../features/assets/appliedTextOverlay.ts)。
   */
  applied_texts?: Record<string, string> | null;
  /** 追溯欄(docs/86 §6.1):`source="template_demo"` 時填來源範本 id。 */
  source_template_id?: string | null;
  // ── 目錄化 typed 欄(BE 2026-08-02;docs/57 §4b 2026-08-10 澄清用法)──
  /** 素材角色(`?role=` server-side filter 用);**成品多視角不帶** — 見 `LibraryItemRole`。 */
  role?: LibraryItemRole | null;
  /** 自由標籤(BE 正規化;`?tag=` filter 用)。 */
  tags?: string[] | null;
  /** 分組鍵:同一個產品的多視角共用同一個值(FE 產,建議放 product/subject id)。 */
  group_key?: string | null;
  /** 匯入撞庫標記(docs/89 §3c);同庫內已有相同內容時指向最早那顆 item。 */
  duplicate_of_item_id?: string | null;
}

export interface LibraryItemsListResponseDto {
  items: LibraryItemDto[];
  /**
   * BE 2026-08-02 素材庫目錄化 — 每頁預設 100 筆,`null` / 缺 = 已翻完。
   * **選填**:改版前的 build 不回這欄(FE 讀不到就當一頁收工,行為與改版前一致)。
   */
  next_cursor?: string | null;
}

// ─── camelCase 業務型別 ─────────────────────────────────────────────────────

export interface AssetLibrarySummary {
  libId: string;
  name: string;
  visibility: string;
  groupId: string;
  /** server derive(viewer_cuid == owner_uid);owner-only UI(如刪除)用。 */
  isOwner: boolean;
  /** non-null → 已軟刪除。 */
  deletedDatetime: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * `LibraryItemDto` 的鍵名 runtime 清單 —— 給契約鎖比對用(TS 型別 runtime 不存在)。
 *
 * 對齊 `REPORT_DTO_KEYS` / `SYNC_JOB_DETAIL_DTO_KEYS` 慣例;下面的
 * `_libraryItemKeysExhaustive` 讓「DTO 加了欄但忘了補進這裡」在 **tsc** 就紅,
 * [beContract.test](@/contract/beContract.test.ts) 再拿它對 BE
 * `LibraryItemResponse.model_fields`(方向「BE ⊆ FE」)。
 *
 * R236 補這份的原因:BE `af2a6c6` 加 `duplicate_of_item_id` 時契約鎖全綠 ——
 * 當時 `library_item_fields` 只被「點名三欄」的斷言用到,加欄不會紅。
 */
export const LIBRARY_ITEM_DTO_KEYS = [
  'item_id', 'asset_kind', 'source', 'source_ad_account_cid',
  'source_project_asset_id', 'source_project_id', 'source_project_node_id',
  'source_template_id',
  'gcs_uri', 'metadata', 'hash_method', 'is_creator', 'created_at',
  'check_badge', 'checked_at', 'check_report_id', 'applied_texts', 'ga_uploaded_at',
  'meta_uploaded_at', 'source_project_name', 'role', 'tags', 'group_key',
  'duplicate_of_item_id',
] as const;

/** DTO 加欄但沒補進 `LIBRARY_ITEM_DTO_KEYS` → 這一行 tsc 紅(對齊 sync DTO 慣例)。 */
type _MissingLibraryItemKey = Exclude<
  keyof LibraryItemDto,
  (typeof LIBRARY_ITEM_DTO_KEYS)[number]
>;
const _libraryItemKeysExhaustive: _MissingLibraryItemKey extends never ? true : never = true;
void _libraryItemKeysExhaustive;

export interface LibraryItem {
  itemId: string;
  assetKind: LibraryItemKind;
  source: LibraryItemSource;
  sourceAdAccountCid: string | null;
  /** `source="project_asset"` 時填反查欄(canvas 成品 → 素材庫,MR !41 S-c)。 */
  sourceProjectAssetId: string | null;
  sourceProjectId: string | null;
  /** `source="canvas_node"` 時填來源文案藍卡 nid(R270 / ADR-044)。 */
  sourceProjectNodeId: string | null;
  gcsUri: string;
  metadata: Record<string, unknown>;
  hashMethod: Record<string, string>;
  isCreator: boolean;
  createdAt: string;
  /**
   * 伺服端最後一次檢查的燈號(docs/88 §9)。**null = 未檢查**。
   * 清單顯示是「批次 store 優先、這欄墊底」——store 是剛跑完那批的即時結果(比 list
   * API 新),這欄則讓重載/換頁後仍看得到已檢查狀態(否則使用者會重跑而重複扣點)。
   */
  checkBadge: CheckBadge | null;
  /** 檢查時間(ISO,可排序);顯示用 `checkBadge.d`(已是 Asia/Taipei 格式化字串)。 */
  checkedAt: string | null;
  /** 推上 Google Ads 素材庫的時間(ISO);null = 尚未上傳。 */
  gaUploadedAt: string | null;
  /** 推上 Meta 素材庫的時間(ISO);null = 尚未上傳。與 {@link gaUploadedAt} 互相獨立。 */
  metaUploadedAt: string | null;
  /** 來源專案名稱(僅 `source="project_asset"`;專案已刪 → null)。 */
  sourceProjectName: string | null;
  /** latest 報告 id;報告頁優先用它取報告,免依賴 batch store 與 composite index。 */
  checkReportId: string | null;
  /**
   * 檢查報告套用的修改建議(TEXT 素材才有;null = 沒套用)。
   * R419.1 起套用已寫回素材檔;只有舊資料(只存報告、沒寫進檔)要疊 —— 一律走
   * [appliedTextOverlay](../features/assets/appliedTextOverlay.ts),不要各處自己套:
   * 素材本體被改過(`metadata.edited_at` 晚於檢查)時這份的列序已對不上,要整份不套。
   */
  appliedTexts: Readonly<Record<string, string>> | null;
  /** 來源範本 id(僅 `source="template_demo"`)。 */
  sourceTemplateId: string | null;
  /**
   * 素材角色(typed;BE 2026-08-02 目錄化)。
   * ⚠️ **2026-08-09 起素材庫預設清單不回帶 role 的 item**(那是給畫布選取器 `?role=`
   * 用的參考素材);所以「要出現在素材庫頁的成品」上傳時**不可**帶 role。
   */
  role: LibraryItemRole | null;
  /** 自由標籤(typed;BE 會正規化去重)。 */
  tags: string[];
  /**
   * 分組鍵(typed;同產品多視角共用一個值)。
   * **這是「產品卡」的正式分組依據**(docs/57 §4b);metadata 內的 `subject_id` /
   * `product_id` 是 R184–R186 的 FE 自訂前身,存量 item 只有那個,讀路徑兩者都認。
   * ⚠️ typed 欄無回填端點(PATCH 改不到),存量散件要成組只能重傳。
   */
  groupKey: string | null;
  /**
   * 匯入撞庫標記(R236;docs/89 §3c)—— 這顆是從廣告帳戶匯入時,發現**同一個素材庫內**
   * 已有相同內容的素材,指向**最早那顆**的 item id;沒撞庫 / 非匯入路徑 → null。
   *
   * 判定:圖片 = 檔案內容 sha256;YouTube = 影片 id(縮圖會被換,不可靠)。
   * 範圍**只在匯入的目標庫內**(同工作區的其他庫不查 —— BE owner 拍板)。
   *
   * ⚠️ **標的可能已經被刪** → 顯示要容錯(BE 不做級聯清理)。
   */
  duplicateOfItemId: string | null;
}

// ─── DTO → camelCase mappers(邊界 normalize)────────────────────────────────

export function mapAssetLibrarySummaryDto(dto: AssetLibrarySummaryDto): AssetLibrarySummary {
  return {
    libId: dto.lib_id,
    name: dto.name,
    visibility: dto.visibility,
    groupId: dto.group_id,
    isOwner: dto.is_owner ?? false,
    deletedDatetime: dto.deleted_datetime ?? null,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  };
}

export function mapAssetLibrariesListResponseDto(
  dto: AssetLibrariesListResponseDto,
): AssetLibrarySummary[] {
  return (dto.items ?? []).map(mapAssetLibrarySummaryDto);
}

/** 套用版:只收字串值(壞值丟掉不猜);空 / 缺欄 → null(= 沒套用)。 */
function mapAppliedTexts(raw: unknown): Readonly<Record<string, string>> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === 'string') out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function mapLibraryItemDto(dto: LibraryItemDto): LibraryItem {
  return {
    itemId: dto.item_id,
    assetKind: dto.asset_kind,
    source: dto.source,
    sourceAdAccountCid: dto.source_ad_account_cid ?? null,
    sourceProjectAssetId: dto.source_project_asset_id ?? null,
    sourceProjectNodeId: dto.source_project_node_id ?? null,
    sourceProjectId: dto.source_project_id ?? null,
    gcsUri: dto.gcs_uri,
    metadata: dto.metadata ?? {},
    hashMethod: dto.hash_method ?? {},
    isCreator: dto.is_creator ?? false,
    createdAt: dto.created_at,
    // 未知 `s` 值 → mapCheckBadgeDto 回 null(= 當未檢查,不猜燈號)
    checkBadge: mapCheckBadgeDto(dto.check_badge),
    checkedAt: dto.checked_at ?? null,
    gaUploadedAt: dto.ga_uploaded_at ?? null,
    metaUploadedAt: dto.meta_uploaded_at ?? null,
    sourceProjectName: dto.source_project_name ?? null,
    checkReportId: dto.check_report_id ?? null,
    appliedTexts: mapAppliedTexts(dto.applied_texts),
    sourceTemplateId: dto.source_template_id ?? null,
    // typed 目錄欄:舊 build 不回這幾欄 → 讀不到當「沒角色 / 沒標籤 / 沒分組」
    role: isLibraryItemRole(dto.role) ? dto.role : null,
    tags: Array.isArray(dto.tags) ? dto.tags.filter((t): t is string => typeof t === 'string') : [],
    groupKey: typeof dto.group_key === 'string' && dto.group_key ? dto.group_key : null,
    duplicateOfItemId: dto.duplicate_of_item_id ?? null,
  };
}

export function mapLibraryItemsListResponseDto(
  dto: LibraryItemsListResponseDto,
): LibraryItem[] {
  return (dto.items ?? []).map(mapLibraryItemDto);
}

// ─── docs/59 #1 — 素材庫 → 畫布匯入(import_from_library)────────────────────

/** `POST /projects/{pid}/assets/import_from_library` response(snake wire)。 */
export interface LibraryImportedAssetDto {
  asset_id: string;
  asset_kind: LibraryItemKind;
  source: 'library_import';
  source_library_item_id: string;
  source_library_id: string;
  gcs_uri: string;
}

/** #1 匯入結果 — `assetId` 綁 canvas node;顯示走 project-scoped download-url。 */
export interface LibraryImportedAsset {
  assetId: string;
  assetKind: LibraryItemKind;
  source: 'library_import';
  sourceLibraryItemId: string;
  sourceLibraryId: string;
  gcsUri: string;
}

export function mapLibraryImportedAssetDto(dto: LibraryImportedAssetDto): LibraryImportedAsset {
  return {
    assetId: dto.asset_id,
    assetKind: dto.asset_kind,
    source: dto.source,
    sourceLibraryItemId: dto.source_library_item_id,
    sourceLibraryId: dto.source_library_id,
    gcsUri: dto.gcs_uri,
  };
}

// ─── docs/57 §3b — 批次匯入(import_from_library_batch;BE 2026-08-10 `47d0dd9`)──

/** 批次匯入單筆失敗原因 —— 值域同單筆端點;未知字串原樣留著給 errorMap 兜底。 */
export interface LibraryImportFailureDto {
  lib_id: string;
  item_id: string;
  error: string;
}

export interface LibraryImportBatchResponseDto {
  /** 去重後的請求筆數(BE 會把重複的 `(lib_id,item_id)` 併成一筆)。 */
  total: number;
  imported_count: number;
  imported: Array<LibraryImportedAssetDto & { lib_id: string; item_id: string }>;
  failed: LibraryImportFailureDto[];
}

/** 批次匯入結果:成功筆帶回原始 ref(caller 要拿它對回自己的勾選)。 */
export interface LibraryImportBatchResult {
  total: number;
  importedCount: number;
  imported: Array<LibraryImportedAsset & { libId: string; itemId: string }>;
  failed: Array<{ libId: string; itemId: string; error: string }>;
}

export function mapLibraryImportBatchResponseDto(
  dto: LibraryImportBatchResponseDto,
): LibraryImportBatchResult {
  const imported = (dto.imported ?? []).map((d) => ({
    ...mapLibraryImportedAssetDto(d),
    libId: d.lib_id ?? d.source_library_id,
    itemId: d.item_id ?? d.source_library_item_id,
  }));
  return {
    total: typeof dto.total === 'number' ? dto.total : imported.length,
    importedCount: typeof dto.imported_count === 'number' ? dto.imported_count : imported.length,
    imported,
    failed: (dto.failed ?? []).map((f) => ({
      libId: f.lib_id,
      itemId: f.item_id,
      error: f.error,
    })),
  };
}

// ─── docs/59 #4 — group 全庫 items 聚合 ─────────────────────────────────────

export interface GroupLibraryItemDto extends LibraryItemDto {
  lib_id: string;
}

export interface GroupLibraryItemsResponseDto {
  items: GroupLibraryItemDto[];
  library_count: number;
  /**
   * BE 2026-08-02 目錄化:聚合端點每次最多回 500 筆,`null` / 缺 = 已翻完。
   * **選填**:改版前的 build 不回這欄(讀不到就一頁收工,行為與改版前一致)。
   */
  next_cursor?: string | null;
}

/** #4 聚合元素 — 標準 item 外加 `libId`(打 #2 download-url 需 libId+itemId)。 */
export interface GroupLibraryItem extends LibraryItem {
  libId: string;
}

export interface GroupLibraryItemsResponse {
  items: GroupLibraryItem[];
  /** 有 item 的庫數。 */
  libraryCount: number;
}

export function mapGroupLibraryItemsResponseDto(
  dto: GroupLibraryItemsResponseDto,
): GroupLibraryItemsResponse {
  return {
    items: (dto.items ?? []).map((d) => ({ ...mapLibraryItemDto(d), libId: d.lib_id })),
    libraryCount: dto.library_count ?? 0,
  };
}

// ===== 素材庫總覽(R240;`GET /asset_libraries/overview`,docs/58 2026-08-15 增補)=====

/**
 * 庫的種類。`ads_auto` / `template_demo` 是**系統自動庫**(`hidden=true`)——
 * 它們不進一般庫清單也不進跨庫聚合(BE `service.py:325` / `:654-658` 在 fan-out
 * 之前就用 `SYSTEM_LIB_PREFIXES` 剔除),總覽是它們唯一的「看得到」入口。
 */
export type AssetLibraryKind = 'user' | 'ads_auto' | 'template_demo';

/** 已知的庫種類(未知值 fail-soft 顯原字串,不猜)。 */
export const ASSET_LIBRARY_KINDS: readonly AssetLibraryKind[] = [
  'user',
  'ads_auto',
  'template_demo',
];

export function isAssetLibraryKind(v: string): v is AssetLibraryKind {
  return (ASSET_LIBRARY_KINDS as readonly string[]).includes(v);
}

export interface AssetLibraryOverviewEntryDto {
  lib_id: string;
  name: string;
  kind: string;
  hidden: boolean;
  item_count: number;
  counts_by_source: Record<string, number>;
  created_at: string | null;
}

export interface AssetLibraryOverviewEntry {
  libId: string;
  name: string;
  /** wire 原值(未知種類不轉換,顯示層自己 gate)。 */
  kind: string;
  hidden: boolean;
  /**
   * ⚠️ **含軟刪**(BE `repository.py:290-307` 用 count 聚合,讀取端濾不掉;
   * `service.py:1063` 明說「count 為聚合查詢含軟刪」)→ UI **不可**講成
   * 「目前有 N 筆」,那與點進去看到的清單筆數對不起來。當容量視角用。
   */
  itemCount: number;
  /** per-source 筆數;BE 只放**非零**的 source(`if n:`)。 */
  countsBySource: Record<string, number>;
  createdAt: string | null;
}

export interface AssetLibraryOverviewResponseDto {
  libraries: AssetLibraryOverviewEntryDto[];
}

/**
 * ⚠️ **這支沒有 pydantic response model**(BE `service.py:1077-1085` 是手組 dict)
 * → 契約鎖 dump 不到它,BE 改欄名 FE 只會靜默壞。所以 mapper 一律 fail-soft:
 * 缺欄用安全預設,不 throw。
 */
export function mapAssetLibraryOverviewEntryDto(
  dto: AssetLibraryOverviewEntryDto,
): AssetLibraryOverviewEntry {
  return {
    libId: String(dto.lib_id ?? ''),
    name: String(dto.name ?? ''),
    kind: String(dto.kind ?? ''),
    hidden: Boolean(dto.hidden),
    itemCount: typeof dto.item_count === 'number' ? dto.item_count : 0,
    countsBySource:
      dto.counts_by_source && typeof dto.counts_by_source === 'object' ? dto.counts_by_source : {},
    createdAt: typeof dto.created_at === 'string' ? dto.created_at : null,
  };
}

export function mapAssetLibraryOverviewResponseDto(
  dto: AssetLibraryOverviewResponseDto,
): AssetLibraryOverviewEntry[] {
  return (dto.libraries ?? []).map(mapAssetLibraryOverviewEntryDto);
}

/**
 * 工作區的廣告素材自動庫 id —— **deterministic,FE 自己拼,沒有查詢端點**
 * (BE `service.py:215`:`ads-lib-{gid}`,對齊平台庫 `hai-lib-{gid}` 慣例)。
 *
 * ⚠️ 這顆庫**不會**出現在 `listLibraries()` / `listGroupItems()` 的結果裡(系統庫在
 * BE 端就被剔除)—— 那是設計不是 bug。要看它的內容只能指名取。
 * 404 = 該工作區還沒有任何帳戶完成過同步。
 */
export function adsAutoLibraryId(groupId: string): string {
  return `ads-lib-${groupId}`;
}

/** 是不是系統自動庫(廣告素材 / 範本示範)—— 這些庫的名稱查不到,要自己給。 */
export function isAdsAutoLibraryId(libId: string): boolean {
  return libId.startsWith('ads-lib-');
}

// ─── TEXT item 內容(R277;BE docs/58 §2.3b `GET …/text-content`)────────────────

/**
 * 回應是 `kind` 判別的 union(**只出現該分支的鍵**;BE router docstring):
 *   `{"kind":"copy_assets_v1","assets":{…7 版位…},"meta":{…}}`
 *   `{"kind":"plain_text","plain_text":"…"}`(舊 `{"text":…}` / raw txt 自動降級)
 */
export interface LibraryTextContentDto {
  kind: string;
  assets?: CopyAssetsDto | null;
  meta?: Record<string, unknown> | null;
  plain_text?: string | null;
}

export type LibraryTextContent =
  | { kind: 'copyAssets'; assets: CopyAssets }
  | { kind: 'plainText'; text: string };

/**
 * 未知 `kind`(BE 之後可能加 v2 envelope)→ **null,呼叫端顯「讀取失敗」**,
 * 不硬塞 plain_text 分支(把結構化內容攤成一坨字串比讀不到更難察覺出錯)。
 */
export function mapLibraryTextContentDto(dto: LibraryTextContentDto): LibraryTextContent | null {
  if (dto.kind === 'copy_assets_v1' && dto.assets) {
    return { kind: 'copyAssets', assets: mapCopyAssetsDto(dto.assets) };
  }
  if (dto.kind === 'plain_text') {
    return { kind: 'plainText', text: dto.plain_text ?? '' };
  }
  return null;
}
